/**
 * Daily Digest — pure helpers (no supabase/AI). Unit-tested in
 * dailyDigestPure.test.ts; consumed by dailyDigest.ts (the data/AI layer).
 */

export type PeriodKey = "24h" | "3d" | "7d" | "30d";

export const PERIODS: Array<{ key: PeriodKey; label: string; hours: number }> = [
  { key: "24h", label: "Last 24 hours", hours: 24 },
  { key: "3d", label: "Last 3 days", hours: 72 },
  { key: "7d", label: "Last 7 days", hours: 168 },
  { key: "30d", label: "Last 30 days", hours: 720 },
];

export interface ActivityItem {
  kind: "thread" | "source" | "knowledge";
  id: string;
  title: string;
  updatedAt: string; // ISO
}

/** Cutoff ISO for a period, computed from an explicit `now` (testable). */
export function cutoffIso(period: PeriodKey, nowMs: number): string {
  const hours = PERIODS.find((p) => p.key === period)?.hours ?? 24;
  return new Date(nowMs - hours * 3600_000).toISOString();
}

export function parseWhen(v: unknown): number {
  const t = Date.parse(String(v ?? ""));
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Build the summarization prompt. Surface-level bullets only — a fallback
 * reference of what the user touched, not a deep analysis.
 */
export function buildDigestPrompt(items: ActivityItem[], periodLabel: string): string {
  const lines = items.map((i) => {
    const tag = i.kind === "thread" ? "Chat" : i.kind === "source" ? "Doc" : "KB";
    return `- [${tag}] ${i.title || "(untitled)"}`;
  });
  return [
    `Below is a list of the chats and documents I worked on during: ${periodLabel}.`,
    `Write a brief, high-level journal entry so I can quickly remember what I was doing.`,
    `Rules: 4-8 short bullet points, surface-level themes only (group related items), plain language, no preamble, no headings. If little activity, say so in one line.`,
    ``,
    `ACTIVITY:`,
    lines.join("\n") || "(no activity)",
  ].join("\n");
}
